import type { ChipColorEntry } from '@/lib/types';

export interface ChipDetection {
  className: string;
  bbox: { x: number; y: number; width: number; height: number };
}

export interface DetectionResult {
  chips: ChipDetection[];
}

// Minimum blob area (as fraction of total image area) to count as a chip
const MIN_BLOB_FRACTION = 0.0005;

/**
 * RGB → HSV conversion.
 * h: 0-360, s: 0-1, v: 0-1
 */
export function rgbToHsv(r: number, g: number, b: number): { h: number; s: number; v: number } {
  const rf = r / 255;
  const gf = g / 255;
  const bf = b / 255;
  const max = Math.max(rf, gf, bf);
  const min = Math.min(rf, gf, bf);
  const delta = max - min;

  let h = 0;
  if (delta !== 0) {
    if (max === rf) h = 60 * (((gf - bf) / delta) % 6);
    else if (max === gf) h = 60 * ((bf - rf) / delta + 2);
    else h = 60 * ((rf - gf) / delta + 4);
  }
  if (h < 0) h += 360;

  const s = max === 0 ? 0 : delta / max;
  return { h, s, v: max };
}

/**
 * Convert hex string to integer RGB values.
 */
export function hexToRgb(hex: string): { r: number; g: number; b: number } {
  const clean = hex.replace('#', '');
  const num = parseInt(clean, 16);
  return { r: (num >> 16) & 255, g: (num >> 8) & 255, b: num & 255 };
}

function getColorRanges(colorEntry: ChipColorEntry) {
  const { r, g, b } = hexToRgb(colorEntry.hex);
  const target = rgbToHsv(r, g, b);
  const tol = colorEntry.tolerance;

  return {
    hMin: (target.h - tol + 360) % 360,
    hMax: (target.h + tol) % 360,
    sMin: 0.1,
    vMin: 0.1,
  };
}

function hueInRange(h: number, hMin: number, hMax: number): boolean {
  if (hMin <= hMax) return h >= hMin && h <= hMax;
  return h >= hMin || h <= hMax;
}

/**
 * Flood-fill a binary mask in-place starting at seed position.
 * Returns the pixel count of the blob.
 * mask: 1 = unchecked candidate, 2 = visited (part of current blob), 0 = background
 */
function floodFillBlob(
  mask: Uint8Array,
  width: number,
  height: number,
  seedIdx: number,
): { count: number; minX: number; maxX: number; minY: number; maxY: number } {
  if (mask[seedIdx] !== 1) return { count: 0, minX: 0, maxX: 0, minY: 0, maxY: 0 };

  const stack: number[] = [seedIdx];
  mask[seedIdx] = 2;
  let count = 0;
  let minX = seedIdx % width;
  let maxX = minX;
  let minY = Math.floor(seedIdx / width);
  let maxY = minY;

  while (stack.length > 0) {
    const cur = stack.pop()!;
    count++;
    const x = cur % width;
    const y = Math.floor(cur / width);

    if (x < minX) minX = x;
    if (x > maxX) maxX = x;
    if (y < minY) minY = y;
    if (y > maxY) maxY = y;

    // 4-connected neighbors
    if (x > 0 && mask[cur - 1] === 1) { mask[cur - 1] = 2; stack.push(cur - 1); }
    if (x < width - 1 && mask[cur + 1] === 1) { mask[cur + 1] = 2; stack.push(cur + 1); }
    if (y > 0 && mask[cur - width] === 1) { mask[cur - width] = 2; stack.push(cur - width); }
    if (y < height - 1 && mask[cur + width] === 1) { mask[cur + width] = 2; stack.push(cur + width); }
  }

  return { count, minX, maxX, minY, maxY };
}

/**
 * Detect chips in an ImageData by HSV color segmentation + blob counting.
 *
 * Algorithm:
 * 1. Convert each pixel to HSV
 * 2. For each configured color, build a binary mask of pixels within the HSV range
 * 3. Flood-fill each mask to find contiguous blobs
 * 4. Each blob larger than MIN_BLOB_FRACTION is counted as one chip
 * 5. Compute bounding box from min/max x,y tracked during flood fill
 */
export function detectChips(imageData: ImageData, colorConfigs: ChipColorEntry[]): DetectionResult {
  const { width, height } = imageData;
  const pixels = imageData.data;
  const totalArea = width * height;
  const minBlobPixels = Math.max(10, totalArea * MIN_BLOB_FRACTION);

  if (colorConfigs.length === 0) return { chips: [] };

  const colorRanges = colorConfigs.map(cc => ({ config: cc, ranges: getColorRanges(cc) }));

  // Build per-color binary masks
  const masks: Uint8Array[] = colorConfigs.map(() => new Uint8Array(totalArea));

  for (let i = 0; i < totalArea; i++) {
    const offset = i * 4;
    const { h, s, v } = rgbToHsv(pixels[offset], pixels[offset + 1], pixels[offset + 2]);

    for (let ci = 0; ci < colorRanges.length; ci++) {
      const { ranges } = colorRanges[ci];
      if (s >= ranges.sMin && v >= ranges.vMin && hueInRange(h, ranges.hMin, ranges.hMax)) {
        masks[ci][i] = 1;
      }
    }
  }

  // Flood-fill each mask to count blobs
  const chips: ChipDetection[] = [];

  for (let ci = 0; ci < colorConfigs.length; ci++) {
    const mask = masks[ci];
    const className = colorConfigs[ci].label;

    for (let i = 0; i < totalArea; i++) {
      if (mask[i] !== 1) continue;

      const { count, minX, maxX, minY, maxY } = floodFillBlob(mask, width, height, i);
      if (count >= minBlobPixels) {
        chips.push({
          className,
          bbox: {
            x: minX / width,
            y: minY / height,
            width: (maxX - minX + 1) / width,
            height: (maxY - minY + 1) / height,
          },
        });
      }
      // floodFillBlob sets visited pixels to 2, which we don't check again
    }

    // Reset remaining 2's to 0 for cleanliness (not strictly needed)
    for (let i = 0; i < totalArea; i++) {
      if (mask[i] === 2) mask[i] = 0;
    }
  }

  return { chips };
}
